import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, isNull } from "underscore";
import useAppContext from "../../context/hooks/useAppContext";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import { useAppDispatch, useAppSelector } from "../../store";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import * as parkingProductsThunks from "../../store/actions/parkingProducts.thunks";
import Warning from "./Warning";
import {
    selectDefaultRevocationEmailValue,
    selectEnableDelegationsScheduling,
} from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import { deleteEntryRightDelegees } from "../../services/entryRights.service";

interface IFormProps {
    getWarningText: (parkingProductsCount: number) => string;
    getWarningDetails: (parkingProductsCount: number) => string;
    getRevocationEmailText: () => string;
    onRefresh: () => void;
}

const DeleteDelegeeWarning = ({
    getWarningText,
    getWarningDetails,
    getRevocationEmailText,
    onRefresh,
}: IFormProps) => {
    const { t } = useTranslation("globals");
    const dispatch = useAppDispatch();
    const delegeeDetails = useAppSelector((state) => state.parkingProducts.delegeeDetails);

    const {
        fleetManagerState,
        toggleDeleteDelegeeWarning,
        setSelectedDelegeeId: setSelectedRegistrationId,
        toggleEditDelegee,
    } = useFleetManagerContext();
    const { appState } = useAppContext();
    const defaultEmailValue = useAppSelector(selectDefaultRevocationEmailValue);
    const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);

    const [sendRevocationEmail, setSendRevocationEmail] = useState(defaultEmailValue);

    const disabled = delegeeDetails.data == null;
    const parkingRightsCount = delegeeDetails.data?.products.length ?? 0;
    const warningText = getWarningText(parkingRightsCount);
    const warningDetails = getWarningDetails(parkingRightsCount);
    const revocationEmailText = getRevocationEmailText();

    const initWarning = () => {
        const { selectedDelegeeId } = fleetManagerState;
        const shouldGetdelegeeDetails =
            isNull(delegeeDetails.data) ||
            delegeeDetails.data?.registrationId !== selectedDelegeeId;
        if (
            shouldGetdelegeeDetails &&
            typeof selectedDelegeeId === "string" &&
            !isEmpty(selectedDelegeeId)
        ) {
            dispatch(
                parkingProductsThunks.getDelegeeDetailsThunk({
                    registrationId: selectedDelegeeId,
                    language: appState.selectedLanguage,
                    seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId!,
                })
            );
        }
    };

    const resetWarning = () => {
        const shouldCleardelegeeDetails = !fleetManagerState.showDelegeeDetails;
        if (shouldCleardelegeeDetails) {
            dispatch(parkingProductsActions.clearDelegeeDetails());
            setSelectedRegistrationId("");
        }
    };

    useEffect(() => {
        initWarning();

        return () => {
            resetWarning();
        };

        // eslint-disable-next-line
    }, []);

    const handleDeleteCancelation = () => {
        toggleDeleteDelegeeWarning();
    };

    const handleSuccessfulDelete = () => {
        toggleDeleteDelegeeWarning();

        if (fleetManagerState.showDelegeeDetails) {
            toggleEditDelegee("");
        }

        onRefresh();
    };

    const [loading, setLoading] = useState(false);

    const handleDeleteConfirmation = () => {
        const { selectedDelegeeId } = fleetManagerState;

        if (typeof selectedDelegeeId !== "string" || isEmpty(selectedDelegeeId)) {
            throw new Error("selectedDelegeeId is not non empty string");
        }

        setLoading(true);

        if (enableDelegationsScheduling) {
            dispatch(
                deleteEntryRightDelegees(
                    {
                        delegeePmcIds: delegeeDetails.data?.products.map((x) => x.pmcId) || [],
                        model: {
                            customerId: appState.user.seasonTicketOwnerCrmId!,
                            delegeeIds: [selectedDelegeeId],
                            sendRevocationEmails: sendRevocationEmail,
                        },
                    },
                    () => {
                        setLoading(false);
                        handleSuccessfulDelete();
                    }
                )
            );
            return;
        }

        dispatch(
            parkingProductsActions.deleteDelegee(
                selectedDelegeeId,
                appState.user.seasonTicketOwnerCrmId as string,
                sendRevocationEmail,
                () => {
                    setLoading(false);
                    handleSuccessfulDelete();
                }
            )
        );
    };

    const hasProducts = parkingRightsCount > 0;

    return (
        <Warning
            warning={warningText}
            details={warningDetails}
            onCancel={handleDeleteCancelation}
            onConfirm={handleDeleteConfirmation}
            disabled={disabled}
            confirmText={t("delete")}
            loading={loading}
        >
            {hasProducts && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                    <qp-switch
                        data-size="s"
                        data-checked={sendRevocationEmail}
                        onClick={() => setSendRevocationEmail(!sendRevocationEmail)}
                    />
                    <span>{revocationEmailText}</span>
                </div>
            )}
        </Warning>
    );
};

export default DeleteDelegeeWarning;
